import React from 'react';
import { TitledVerticalFlexLayout } from '../../features/shared/components/VerticalFixedLayout/VerticalFixedLayout';
import { useAskMeAnything } from '../../features/askMeAnything/hooks/useAskMeAnything';
import { AskMeAnythingProvider } from '../../features/askMeAnything/context/AskMeAnythingProvider';
import { Questions } from '../../features/askMeAnything/questions';
import { ChatItem } from '../../services/types';
import { Button, Input, Menu, Empty, Flex, Typography, Tag, Dropdown } from 'antd';
import { Chat, CHATS_STORAGE_KEY, CHAT_ID_PREFIX } from './types';
import { useEffect, useState } from 'react';
import {
  PlusOutlined,
  MessageOutlined,
  MoreOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { AccountSearchProvider } from '../../features/account/account-search/account-search.context';
import { AccountOverviewProvider } from '../../features/account/account-overview/account-overview.context';
import { MenuItem } from './styles';
import { AskMeAnythingChat3 } from '../../features/shared/components/AskMeAnything/AskMeAnythingChat3';

export const AISearch: React.FC = () => {
  const { askInsights } = useAskMeAnything();
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingChatName, setEditingChatName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const storedChats = localStorage.getItem(CHATS_STORAGE_KEY);
    if (storedChats) {
      const parsedChats = JSON.parse(storedChats);
      setChats(parsedChats);
      if (parsedChats.length > 0 && !selectedChat) {
        setSelectedChat(parsedChats[0].chatId);
      }
    }
  }, []);

  const saveChats = (updatedChats: Chat[]) => {
    localStorage.setItem(CHATS_STORAGE_KEY, JSON.stringify(updatedChats));
    setChats(updatedChats);
  };

  const createNewChat = () => {
    const newChat: Chat = {
      chat: `New Chat (${chats.length + 1})`,
      chatId: `${CHAT_ID_PREFIX}${Date.now()}`,
      createdAt: new Date().toISOString(),
    };

    const updatedChats = [newChat, ...chats];
    saveChats(updatedChats);
    setSelectedChat(newChat.chatId);
  };

  return (
    <TitledVerticalFlexLayout title={'UpdateAI AI Assistant'}>
      <Tag
        color="red"
        bordered={false}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '16px',
        }}
      >
        <strong>Currently in Alpha</strong> — share any and all feedback to{' '}
        <a
          href="mailto:concierge@update.ai"
          style={{ color: 'red !important', textDecoration: 'underline !important' }}
        >
          concierge@update.ai
        </a>
      </Tag>

      <div style={{ display: 'flex', gap: '16px', marginTop: '16px', height: 'calc(100% - 50px)' }}>
        <div
          style={{
            width: '300px',
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            borderRadius: '4px',
            padding: '16px',
            gap: '16px',
          }}
        >
          <div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={createNewChat}
              style={{ width: '100%' }}
            >
              New Chat
            </Button>
          </div>

          <h2 style={{ margin: 0, fontSize: '18px' }}>Chats</h2>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {chats.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No chats yet. Create your first chat to get started!"
              />
            ) : (
              <>
                {(() => {
                  return chats.length > 0 ? (
                    <Menu
                      mode="inline"
                      selectedKeys={selectedChat ? [selectedChat] : []}
                      style={{ borderInlineEnd: '0', padding: '0' }}
                      items={chats.map(chat => ({
                        key: chat.chatId,
                        label: (
                          <MenuItem>
                            <Flex vertical style={{ lineHeight: '16px', flex: 1 }}>
                              {editingChatId === chat.chatId ? (
                                <Input
                                  size="small"
                                  value={editingChatName}
                                  onChange={e => setEditingChatName(e.target.value)}
                                  onPressEnter={() => {
                                    if (editingChatName.trim()) {
                                      const updatedChats = chats.map(c =>
                                        c.chatId === chat.chatId
                                          ? { ...c, chat: editingChatName.trim() }
                                          : c,
                                      );
                                      saveChats(updatedChats);
                                    }
                                    setEditingChatId(null);
                                    setEditingChatName('');
                                  }}
                                  onBlur={() => {
                                    if (editingChatName.trim()) {
                                      const updatedChats = chats.map(c =>
                                        c.chatId === chat.chatId
                                          ? { ...c, chat: editingChatName.trim() }
                                          : c,
                                      );
                                      saveChats(updatedChats);
                                    }
                                    setEditingChatId(null);
                                    setEditingChatName('');
                                  }}
                                  autoFocus
                                  onClick={e => e.stopPropagation()}
                                />
                              ) : (
                                <>
                                  <span>{chat.chat}</span>
                                  <Typography.Text type="secondary" style={{ fontSize: '10px' }}>
                                    {new Date(chat.createdAt).toLocaleDateString()}
                                  </Typography.Text>
                                </>
                              )}
                            </Flex>

                            <Dropdown
                              trigger={['click']}
                              onOpenChange={setIsDropdownOpen}
                              menu={{
                                items: [
                                  {
                                    label: 'Rename',
                                    key: 'rename-chat',
                                    icon: <EditOutlined />,
                                    onClick: () => {
                                      setEditingChatId(chat.chatId);
                                      setEditingChatName(chat.chat);
                                    },
                                  },
                                  {
                                    label: 'Delete',
                                    icon: <DeleteOutlined />,
                                    danger: true,
                                    key: 'delete-chat',
                                    onClick: () => {
                                      const updatedChats = chats.filter(
                                        ({ chatId }) => chatId !== chat.chatId,
                                      );

                                      if (
                                        selectedChat === chat.chatId ||
                                        updatedChats.length === 0
                                      ) {
                                        setSelectedChat(null);
                                      }

                                      saveChats(updatedChats);
                                    },
                                  },
                                ],
                              }}
                            >
                              <Button
                                className="options-menu"
                                type="default"
                                icon={<MoreOutlined />}
                                size="small"
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              />
                            </Dropdown>
                          </MenuItem>
                        ),
                        title: 'title ' + chat.chat,
                        onClick: () => {
                          if (!isDropdownOpen) {
                            setSelectedChat(chat.chatId);
                          }
                        },
                      }))}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No chats found matching your search"
                    />
                  );
                })()}
              </>
            )}
          </div>
        </div>

        <section
          style={{
            background: 'white',
            padding: '16px',
            paddingTop: '0',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          {!selectedChat && (
            <Flex align="center" justify="center" style={{ height: '100%' }} vertical>
              <MessageOutlined style={{ fontSize: '24px' }} />
              <Typography.Title level={3} style={{ marginTop: '4px' }}>
                Select a chat
              </Typography.Title>
              <Typography.Text>
                To start a chat, select one from the list on the left
              </Typography.Text>
              <Typography.Text>if none are available, create a new one</Typography.Text>
            </Flex>
          )}

          {selectedChat && (
            <AskMeAnythingProvider
              amaKey={selectedChat}
              suggestedQuestions={{
                data: Questions.iaDashboard,
                category: 'all',
                displayDropdown: true,
              }}
            >
              <AskMeAnythingChat3
                fullMessageBubble={false}
                location="ai-search"
                askFunction={(chatMessages: ChatItem[]) => askInsights(chatMessages)}
                styles={{
                  height: '100%',
                }}
              />
            </AskMeAnythingProvider>
          )}
        </section>
      </div>
    </TitledVerticalFlexLayout>
  );
};

export const AISearchProviderWrapper = () => {
  return (
    <AccountSearchProvider searchId="ai-search">
      <AccountOverviewProvider>
        <AISearch />
      </AccountOverviewProvider>
    </AccountSearchProvider>
  );
};
